import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { dispatch } from "../redux/redux-config";
import actions from "../actions/actions";
import { GuiTextbox } from "../components/global-ui/gui-textbox";
import { ToastContainer } from "../components/toast";
import { useUpdateState } from "../utilities/utilities";
import LoadingSpinner from "../components/loading-spinner";
import "../styles/reset-password.scss";

const ResetPassword = () => {
  const [state, update] = useUpdateState({
    password: "",
    confirmPassword: "",
    error: "",
    isPasswordValid: false,
    isPasswordMatch: false,
    isLoading: false,
    validatingToken: true,
    tokenValid: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const {
    password,
    confirmPassword,
    error,
    isPasswordValid,
    isPasswordMatch,
    isLoading,
    validatingToken,
    tokenValid,
  } = state;

  // Reset id comes either from the emailed link's URL (?ID=) or, when the user
  // typed a verification code in-app, from Redux (set by passwordRecovery.verify()).
  const urlResetId = new URLSearchParams(window.location.search).get("ID");
  const reduxResetId = useSelector((store) => store.user.resetId);
  const resetId = urlResetId || reduxResetId;

  useEffect(() => {
    if (urlResetId) {
      dispatch("LOGIN_SETTINGS", { resetId: urlResetId });
    }
    document.body.classList.add("reset-password-page");
    document.body.style.backgroundColor = "#b8c9df";
    return () => {
      document.body.classList.remove("reset-password-page");
      document.body.style.backgroundColor = "";
    };
  }, [resetId]);

  useEffect(() => {
    let cancelled = false;

    async function checkToken() {
      if (!resetId) {
        update({ validatingToken: false, tokenValid: false });
        return;
      }
      // Validate the reset link upfront so an expired/used link goes straight to the
      // "link expired" screen instead of showing the password form first.
      const result = await actions.passwordRecovery.validateResetToken(resetId);
      if (cancelled) return;
      // Fail open: only treat as invalid when the server explicitly confirms it (the
      // action already redirects to LinkExpired in that case). A failed/errored check
      // (e.g. network issue) should not block the form — submit still validates for real.
      const explicitlyInvalid = result?.success && result.isValid === false;
      update({
        validatingToken: false,
        tokenValid: !explicitlyInvalid,
      });
    }

    checkToken();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resetId]);


  const validatePassword = (password) => {
    const regex = /^(?=.*[A-Z])(?=.*\d)(?=.*[@#$%^&*!])[A-Za-z\d@#$%^&*!]{10,}$/;
    return regex.test(password);
  };

  const updateValue = (name, value) => {
    const isValid =
      name === "password" ? validatePassword(value) : isPasswordValid;
    update({
      [name]: value,
      isPasswordValid: name === "password" ? isValid : isPasswordValid,
      isPasswordMatch:
        name === "confirmPassword" ? value === password : isPasswordMatch,
      error: null,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isPasswordValid) {
      update({ error: "Please ensure the password meets all requirements." });
      return;
    }

    if (!isPasswordMatch) {
      update({ error: "Passwords do not match. Please try again." });
      return;
    }

    update({ isLoading: true });

    try {
      const res = await actions.passwordRecovery.resetPassword(password);
      if (!res.success) {
        // resetPassword action redirects to LinkExpired for expired/used tokens;
        // for other failures (e.g. same-as-previous-password) show inline error.
        update({
          error: res.errorMessage || "Error resetting the password.",
          isLoading: false,
        });
      }
      // On success the action switches to the "passwordCreated" screen.
    } catch (err) {
      update({ error: "Unexpected error occurred.", isLoading: false });
      console.error(err);
    }
  };

  const isButtonDisabled =
    !isPasswordValid ||
    !isPasswordMatch ||
    !password ||
    !confirmPassword ||
    isLoading;

  // Avoid flashing the password form while the reset link is being validated;
  // an invalid/expired token triggers a redirect to the LinkExpired screen.
  if (validatingToken || !tokenValid) {
    return (
      <div className="reset-password-wrapper">
        <LoadingSpinner show={true} />
      </div>
    );
  }

  return (
    <div className="reset-password-wrapper">
      <div className="reset-password-brand">
        <img src="/lib/images/Vector.svg" alt="High Ground Solutions" />
        <div className="reset-password-brand-caption">
          High Ground Solutions
        </div>
      </div>

      <div className="reset-password-container">
        <button
          type="button"
          className="reset-password-back-btn"
          onClick={() => actions.passwordRecovery.changeScreen("login")}
        >
          <i className="far fa-chevron-left"></i>
          Back to Login
        </button>

        <div className="reset-password-header">
          <h1 className="reset-password-title">Create New Password</h1>
          <p className="reset-password-subtitle">
            Your new password must be different
            <br />
            from your previous one.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="reset-password-form">
          {/* Error Alert */}
          {error && (
            <div className="alert alert-error">
              <i className="fa fa-exclamation-circle"></i>
              <span>{error}</span>
            </div>
          )}

          {/* Password Field */}
          <div className="password-input-wrapper">
            <GuiTextbox
              label="Password"
              name="password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={updateValue}
            />
            <button
              type="button"
              className="visibility-toggle"
              onClick={() => setShowPassword((prev) => !prev)}
              title={showPassword ? "Hide password" : "Show password"}
            >
              <i
                className={`fa ${showPassword ? "fa-eye-slash" : "fa-eye"}`}
              ></i>
            </button>
          </div>

          {/* Password Requirements */}
          <div className="password-requirements-simple">
            <ul>
              <li className={password.length >= 10 ? "met" : ""}>
                10+ characters
              </li>
              <li className={/[A-Z]/.test(password) ? "met" : ""}>
                1 uppercase
              </li>
              <li className={/[0-9]/.test(password) ? "met" : ""}>1 number</li>
              <li className={/[@#$%^&*!]/.test(password) ? "met" : ""}>
                1 special character (@#$%^&*!)
              </li>
            </ul>
          </div>

          {/* Confirm Password Field */}
          <div className="password-input-wrapper">
            <GuiTextbox
              label="Confirm Password"
              name="confirmPassword"
              type={showConfirmPassword ? "text" : "password"}
              value={confirmPassword}
              onChange={updateValue}
            />
            <button
              type="button"
              className="visibility-toggle"
              onClick={() => setShowConfirmPassword((prev) => !prev)}
              title={showConfirmPassword ? "Hide password" : "Show password"}
            >
              <i
                className={`fa ${showConfirmPassword ? "fa-eye-slash" : "fa-eye"}`}
              ></i>
            </button>
          </div>

          {/* Match Status */}
          {confirmPassword && (
            <div
              className={`password-match-status ${isPasswordMatch ? "matched" : "not-matched"}`}
            >
              <span>
                {isPasswordMatch
                  ? "Password matches"
                  : "Passwords do not match"}
              </span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            className="reset-password-btn"
            disabled={isButtonDisabled}
          >
            {isLoading && <i className="fa fa-spinner"></i>}
            Create New Password
          </button>
        </form>
      </div>
      <ToastContainer />
    </div>
  );
};

export default ResetPassword;
